import * as React from 'react';

import { Badge, MultiSelectFilter } from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { useCustomerAccounts } from '../../../_graphql/queryCustomerAccounts';
import { $columnSelectedOutboundCustomerIds } from '../../../_state/transactions.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { FilterOptionLabel } from './FilterOptionLabel';

interface CustomerAccount {
  id: string;
  name: string;
  type?: string;
}

interface OutboundCustomerColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ OUTBOUND CUSTOMER COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const OutboundCustomerColumnFilter: React.FC<
  OutboundCustomerColumnFilterProps
> = ({ onClose }) => {
  const t = useTranslations();

  // States
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    undefined,
  );

  // Atoms
  const [selectedCustomerIdsSet, setSelectedCustomerIdsSet] = useAtom(
    $columnSelectedOutboundCustomerIds,
  );
  const selectedCustomerIds = Array.from(selectedCustomerIdsSet);

  // Memoized values
  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedCustomerIds),
    [selectedCustomerIds],
  );

  // Data hooks
  const {
    data: customers,
    isFetching: isFetchingCustomers,
    isLoading: isLoadingCustomers,
  } = useCustomerAccounts({
    searchTerm,
    limit: 20,
  });

  const { data: selectedCustomers } = useCustomerAccounts({
    accountIds: selectedCustomerIds,
  });

  // Callbacks
  const handleSearch = React.useCallback((query: string) => {
    setSearchTerm(query);
  }, []);

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedCustomerIdsSet(new Set(Array.from(keys) as string[]));
    },
    [setSelectedCustomerIdsSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedCustomerIdsSet(new Set());
  }, [setSelectedCustomerIdsSet]);

  const getLabel = React.useCallback(
    (item: CustomerAccount, isSelected: boolean) => (
      <FilterOptionLabel
        label={item.name}
        badge={
          item.type && (
            <Badge color={item.type === 'Individual' ? 'warning' : 'primary'}>
              {item.type}
            </Badge>
          )
        }
        isSelected={isSelected}
      />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@OutboundCustomerColumnFilter min-w-[350px]"
      entries={customers}
      selectedEntries={selectedCustomers}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(customer) => customer.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.transactionsView.filters.customer.searchPlaceholder}
      isLoading={isLoadingCustomers}
      isFetching={isFetchingCustomers}
    />
  );
};
